import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useLocation, useSearchParams } from "react-router-dom";

import { downloadExcel } from "@src/utils/download-helper";

import {
  GetAllPurchasesAPIParams,
  TransactionType,
  UsePurchasesServiceOperators
} from "@src/lib/types/purchases";

import {
  allGoldPurchasesAPI,
  allPurchasesAPI,
  exportGoldPurchasesListAPI,
  exportPurchasesListAPI,
  totalGoldPurchasesCurrentMonth,
  totalPurchasesCurrentMonth
} from "@src/api/purchases";
import { usePurchasesContext } from "./provider";
import { TABLE_HEAD_GOLD_PURCHASE, TABLE_HEAD_PURCHASE } from "@src/utils/mockdata/table-headers";
import numeral from "numeral";
import { getCurrentMonthDays } from "@src/utils/current-month";
import { redeemStatusName } from "./purchase-records/table-row";

export const usePurchasesService = (): Readonly<UsePurchasesServiceOperators> => {
  const location = useLocation();
  const [params] = useSearchParams();
  const year = params.get("year");
  const month = params.get("month");
  const { state, handlePageChange } = usePurchasesContext();

  const {
    page,
    fromdate,
    todate,
    order,
    orderBy,
    rowsPerPage,
    status,
    paymentOption,
    debouncedSearch,
  } = state || {};
  const newPage = (page || 0) + 1;

  const purchasesListQueryKey = [
    "purchases",
    {
      year,
      month,
      newPage,
      fromdate,
      todate,
      debouncedSearch,
      order,
      orderBy,
      rowsPerPage,
      status,
      paymentOption,
      pathname: location.pathname
    },
  ];

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: purchasesListQueryKey,
    queryFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        size: rowsPerPage,
        page: newPage,
        from_date: startDate,
        to_date: endDate,
      };

      if (debouncedSearch) reqParams.search = debouncedSearch;
      if (status) reqParams.status = status;
      if (orderBy && order) reqParams.sort = `${orderBy},${order}`;
      if (fromdate) reqParams.from_date = fromdate;
      if (todate) reqParams.to_date = todate;
      if (year) reqParams.year = year;
      if (month) reqParams.month = month;

      if(location.pathname === "/dashboard/purchase/points-purchases") reqParams.payment_type_id = 101;
      if(location.pathname === "/dashboard/purchase/cash-purchases") reqParams.purchase_item_type = "PRODUCT";
      if(location.pathname === "/dashboard/purchase/gold-purchases") {
        const res = await allGoldPurchasesAPI(reqParams);

        return res;
      };

      const res = await allPurchasesAPI(reqParams);

      return res;
    },
  });

  const onExportPurchasesList = useMutation({
    mutationKey: ["export-purchases-list"],
    mutationFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        from_date: startDate,
        to_date: endDate,
      };

      if (debouncedSearch) reqParams.search = debouncedSearch;

      if (status) reqParams.status = status;

      if (paymentOption) reqParams.payment_type_id = Number(paymentOption);

      if (orderBy && order) reqParams.sort = `${orderBy},${order}`;

      if (fromdate) reqParams.from_date = fromdate;
      if (todate) reqParams.to_date = todate;
      if (year) reqParams.year = year;
      if (month) reqParams.month = month;

      if(location.pathname === "/dashboard/purchase/points-purchases") reqParams.payment_type_id = 101;
      if(location.pathname === "/dashboard/purchase/cash-purchases") reqParams.purchase_item_type = "PRODUCT";

      let res = null;

      if(location.pathname === "/dashboard/purchase/gold-purchases") {
        res = await exportGoldPurchasesListAPI(reqParams);
      }else{
        res = await exportPurchasesListAPI(reqParams);
      }

      const newJson = res.data.items.map((item: TransactionType) => {
        const result: Record<string, any> = {};
          const headers = location.pathname === "/dashboard/purchase/gold-purchases" ? 
            TABLE_HEAD_GOLD_PURCHASE : TABLE_HEAD_PURCHASE;

        headers.forEach(({ id, label = "" }) => {
          let fieldValue: any;

          if (id === 'id') fieldValue = item.id;
          if (id === 'u.id') fieldValue = item.user.id;
          if (id === 'u.palmsId') fieldValue = item.user.palmsId;
          if (id === 'u.displayName') fieldValue = item.user.displayName;
          if (id === 'purchaseItemName') fieldValue = item.purchaseItemName;
          if (id === 'productName') fieldValue = item.productName;
          if (id === 'amount') fieldValue = numeral(item.amount || 0).format("0,000");
          if (id === 'goldAmount') fieldValue = numeral(item.goldAmount || 0).format("0,000");
          if (id === 'status') fieldValue = item.status;
          if (id === 'redeemStatusID') fieldValue = redeemStatusName(item.redeemStatusID ?? 0);
          if (id === 'transactionDate') fieldValue = item.transactionDate ? moment.parseZone(item.transactionDate).format("MM-DD-YYYY hh:mm A") : "-";
          if (id === 'requestDate') fieldValue = item.requestDate ? moment.parseZone(item.requestDate).format("MM-DD-YYYY hh:mm A") : "-";

          result[label] = fieldValue;
        });

        return result;
      });

      downloadExcel(newJson, `export-${location.pathname.substring(location.pathname.lastIndexOf('/') + 1)}`);
    },
  });

  const { data: totalPurchase, refetch: refetchTotalPurchase } = useQuery({
    queryKey: ["current-month-total-purchases",
      {
        year,
        month,
        newPage,
        fromdate,
        todate,
        debouncedSearch,
        order,
        orderBy,
        rowsPerPage,
        status,
        paymentOption,
        pathname: location.pathname
      }
    ],
    queryFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        from_date: startDate,
        to_date: endDate,
      };

      if (debouncedSearch) reqParams.search = debouncedSearch;
      if (status) reqParams.status = status;
      if (fromdate) reqParams.from_date = fromdate;
      if (todate) reqParams.to_date = todate;
      if (year) reqParams.year = year;
      if (month) reqParams.month = month;
      if (orderBy && order) reqParams.sort = `${orderBy},${order}`;

      if(location.pathname === "/dashboard/purchase/points-purchases") reqParams.payment_type_id = 101;
      if(location.pathname === "/dashboard/purchase/cash-purchases") reqParams.purchase_item_type = "PRODUCT";
      if(location.pathname === "/dashboard/purchase/gold-purchases") {
        const res = await totalGoldPurchasesCurrentMonth(reqParams);

        return {
          count: res.count,
          totalAmount: res.totalGoldAmount,
        };
      };

      const purchaseRes = await totalPurchasesCurrentMonth(reqParams);

      return purchaseRes || {};
    },
  });

  const onRefreshPurchases = () => {
    handlePageChange(null, 0);
    refetch();
    refetchTotalPurchase();
  };

  return {
    data,
    purchases: data?.items || [],
    isLoading,
    isRefreshing: isRefetching,
    totalPurchase,
    onRefreshPurchases,
    onExportPurchasesList,
  };
};
